import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Divider,
    CircularProgress,
    Button,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate, useParams } from "react-router-dom";
import { getCourseData } from "../../services/CourseService";

/* ---------------- COMPONENTS ---------------- */

const OverviewRow = ({ label, value }) => (
    <>
        <Grid size={{ xs: 12, md: 4 }}>
            <Typography fontWeight={700}>{label}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
            {typeof value === "string" ? (
                <Typography>{value || "-"}</Typography>
            ) : (
                value
            )}
        </Grid>
        <Grid size={{ xs: 12 }}>
            <Divider sx={{
                borderColor: "#e0e0e0",   // grey color
                height: "1px",
            }} />
        </Grid>
    </>
);

const BulletRow = ({ text }) => (
    <Box display="flex" alignItems="flex-start" gap={1}>
        <FiberManualRecordIcon
            sx={{ fontSize: 8, color: "#F57C00", mt: "6px" }}
        />
        <Typography>{text}</Typography>
    </Box>
);

/* ---------------- PAGE ---------------- */

const ProgramDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [programData, setProgramData] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (!id) return;

        const fetchProgramData = async () => {
            try {
                const res = await getCourseData(id);
                if (res?.data?.success) {
                    setProgramData(res.data.data);
                }
            } catch (err) {
                console.error("❌ Error fetching program:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData();
    }, [id]);

    const getResponsiveHtml = (html) => {
        if (!html) return "";

        return html
            // ❌ remove inline styles
            .replace(
                /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
                "<$1$2$3>"
            )

            // ❌ remove data attributes
            // .replace(/data-[^=]+="[^"]*"/gi, "")

            // ❌ remove spans
            .replace(/<span[^>]*>/gi, "")
            .replace(/<\/span>/gi, "")

            // ❌ remove empty paragraphs
            .replace(/<p>\s*<\/p>/gi, "")
            .replace(/<p>\s*&nbsp;\s*<\/p>/gi, "")

            // ❌ remove spaces
            .replace(/&nbsp;/gi, " ")
            .replace(/\u00A0/gi, " ")

            // ❌ remove <br>
            .replace(/<br\s*\/?>/gi, "")

            // ✅ FIX: <li><p> → <li>
            // .replace(/<li>\s*<p[^>]*>/gi, "<li>")
            // .replace(/<\/p>\s*<\/li>/gi, "</li>")

            // ✅ ADD CLASS for strong headings
            .replace(/<p>\s*<strong>/gi, '<p class="info-item"><strong>')

            .replace(/\[empty_line\]/gi, "<br />")

            .trim();
    };


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                <CircularProgress color="warning" />
            </Box>
        );
    }

    if (!programData) {
        return (
            <Box sx={{ textAlign: "center", mt: 10 }}>
                <Typography>No study program data found.</Typography>
            </Box>
        );
    }

    const winterDeadline =
        programData.application_winter_deadline &&
            programData.application_winter_deadline !== "-" &&
            programData.application_winter_deadline.trim() !== ""
            ? programData.application_winter_deadline
            : null;

    const summerDeadline =
        programData.application_summer_deadline &&
            programData.application_summer_deadline !== "-" &&
            programData.application_summer_deadline.trim() !== ""
            ? programData.application_summer_deadline
            : null;

    const winterPeriod =
        programData.application_winter_period &&
            programData.application_winter_period !== "-" &&
            programData.application_winter_period.trim() !== ""
            ? programData.application_winter_period
            : null;

    const summerPeriod =
        programData.application_summer_period &&
            programData.application_summer_period !== "-" &&
            programData.application_summer_period.trim() !== ""
            ? programData.application_summer_period
            : null;

    const formatWithBreaks = (text) =>
        text.replace(/\r?\n/g, "<br />");

    return (
        <>
            {/* 🔶 HERO SECTION */}
            <Box
                sx={{
                    background: "linear-gradient(90deg, #053770 0%, #002045 100%)",
                    color: "#fff",
                    py: { xs: 3, sm: 4, md: 6 },
                }}
            >
                <Container maxWidth="lg" sx={{
                    px: { xs: 3, sm: 4, md: 6 },
                    paddingLeft: { xs: "24px !important", md: "48px !important" },
                    paddingRight: { xs: "24px !important", md: "48px !important" }
                }}>
                    <Button
                        color="#ffffff"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/course-finder")} // <-- navigate back
                        sx={{ mb: 2 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h2" sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" } }} fontWeight={700}>
                        {programData.course_name || "-"}
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mt: 1, fontSize: { xs: "14px", sm: "16px" } }}>
                        {programData.university_name || "-"}
                    </Typography>
                </Container>
            </Box>

            {/* 🔷 CONTENT */}
            <Container maxWidth="lg" sx={{
                py: { xs: 3, sm: 4, md: 6 },
                px: { xs: 3, sm: 4, md: 6 },
                paddingLeft: { xs: "24px !important", md: "48px !important" },
                paddingRight: { xs: "24px !important", md: "48px !important" }
            }}>
                <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{ mb: { xs: 2, sm: 4 }, color: "#002045", fontSize: { xs: "22px", sm: "30px" } }}
                >
                    Overview
                </Typography>

                <Grid container spacing={2}>
                    <OverviewRow label="Degree" value={programData.degree_name || "-"} />
                    <OverviewRow label="Intake" value={
                        <Box
                            sx={{
                                "& p": {
                                    margin: 0,
                                },
                            }}
                            dangerouslySetInnerHTML={{
                                __html: getResponsiveHtml(programData.start_of_study) || "-",
                            }}
                        />
                    } />

                    {/* {(programData.application_winter_deadline ||
                        programData.application_summer_deadline) && ( */}
                    <OverviewRow
                        label="Application Deadline"
                        value={
                            <Box className="editor-content">
                                {!winterDeadline && !summerDeadline && <Box>-</Box>}

                                {winterDeadline && (
                                    <>
                                        <strong>Winter</strong>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: formatWithBreaks(winterDeadline),
                                            }}
                                        />
                                    </>
                                )}

                                {winterDeadline && summerDeadline && (
                                    // <Box style={{ marginTop: 16 }} />
                                    <Divider sx={{
                                        borderColor: "#e0e0e0",   // grey color
                                        height: "1px",
                                        marginBlock: "16px"
                                    }} />
                                )}

                                {summerDeadline && (
                                    <>
                                        <strong>Summer</strong>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: formatWithBreaks(summerDeadline),
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                        }
                    />
                    {/* )} */}

                    {/* {(programData.application_winter_period ||
                        programData.application_summer_period) && ( */}
                    <OverviewRow
                        label="Application Period"
                        value={
                            <Box className="editor-content">
                                {!winterPeriod && !summerPeriod && <Box>-</Box>}

                                {winterPeriod && (
                                    <>
                                        <strong>Winter</strong>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: formatWithBreaks(winterPeriod),
                                            }}
                                        />
                                    </>
                                )}

                                {winterPeriod && summerPeriod && (
                                    // <Box style={{ marginTop: 16 }} />
                                    <Divider sx={{
                                        borderColor: "#e0e0e0",   // grey color
                                        height: "1px",
                                        marginBlock: "16px"
                                    }} />
                                )}

                                {summerPeriod && (
                                    <>
                                        <strong>Summer</strong>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: formatWithBreaks(summerPeriod),
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                        }
                    />
                    {/* )} */}



                    <OverviewRow label="Tuition Fee" value={programData.tution_fees_per_semester} />
                    <OverviewRow
                        label="Semester Contribution"
                        value={programData.semester_contribution}
                    />
                    <OverviewRow label="Duration of Study" value={programData.durations || "-"} />
                    <OverviewRow label="Course Credits" value={programData.course_credit || 0} />
                    <OverviewRow
                        label="Admission Restricted"
                        value={
                            programData.admission_restricted === "yes" ? "Yes" : "No"
                        }
                    />
                    <OverviewRow
                        label="Language of Instruction"
                        value={
                            programData.languages || "-"
                        }
                    />
                    <OverviewRow label="Application Type" value={programData.application_name} />
                    {programData.admission_requirement && (<OverviewRow
                        label="Admission Requirements"
                        value={
                            <Box
                                className='editor-content'
                                dangerouslySetInnerHTML={{
                                    __html: getResponsiveHtml(programData.admission_requirement) || "-",
                                }}
                            />
                        }
                    />)}


                    {programData.career_prospect && (<OverviewRow
                        label="Career Prospects"
                        value={
                            <Box
                                className='editor-content'
                                dangerouslySetInnerHTML={{
                                    __html: getResponsiveHtml(programData.career_prospect) || "-",
                                }}
                            />
                        }
                    />)}

                    {programData.curriculum_structure && (<OverviewRow
                        label="Curriculum / Program Structure"
                        value={
                            <Box
                                className='editor-content'
                                dangerouslySetInnerHTML={{
                                    __html: getResponsiveHtml(programData.curriculum_structure) || "-",
                                }}
                            />
                        }
                    />)}

                    {programData.application_portal && (<OverviewRow
                        label="Application Link"
                        value={
                            <Box
                                className='editor-content'
                                dangerouslySetInnerHTML={{
                                    __html: getResponsiveHtml(programData.application_portal) || "-",
                                }}
                            />
                        }
                    />)}
                </Grid>

            </Container>
        </>
    );
};

export default ProgramDetailPage;